import { FC } from "react";

import { Box, Stack } from "@mui/material";

import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";

const FOOTER_BACKGROUND = "#0F0E5B";
const FOOTER_DIVIDER = "#29276A";
const HEADING_TEAL = "#09CFAF";

const SOCIAL_LINKS = [
  {
    label: "Facebook",
    href: "https://www.facebook.com/zeffyplatform",
    icon: "https://cdn.prod.website-files.com/60af7f6d21134db12548f5b9/6359a43e9fb3ae474009ad11_facebook.svg",
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/zeffyplatform/?hl=fr",
    icon: "https://cdn.prod.website-files.com/60af7f6d21134db12548f5b9/6359a43e9fb3ae861709ad10_instagram.svg",
  },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/zeffy/",
    icon: "https://cdn.prod.website-files.com/60af7f6d21134db12548f5b9/6359a43eb1a423b615f1682f_linkedin.svg",
  },
];

const LINK_COLUMNS = [
  {
    heading: "Top grants by cause",
    links: [
      { label: "Grants for churches", href: "/grant-finder/grants/churches" },
      { label: "Grants for student clubs", href: "/grant-finder/grants/student-clubs-orgs" },
      { label: "Grants for food pantries", href: "/grant-finder/grants/food-pantries" },
      {
        label: "Grants for animal shelters",
        href: "/grant-finder/grants/animal-shelters-and-rescue-organizations",
      },
      { label: "Grants for private schools", href: "/grant-finder/grants/private-schools" },
      { label: "Grants for youth development", href: "/grant-finder/grants/youth-development-programs" },
      { label: "See all causes", href: "/grant-finder/grant-index#causes" },
    ],
  },
  {
    heading: "Top grants by location",
    links: [
      { label: "Grants in California", href: "/grant-finder/grants/california" },
      { label: "Grants in Texas", href: "/grant-finder/grants/texas" },
      { label: "Grants in Florida", href: "/grant-finder/grants/florida" },
      { label: "Grants in Illinois", href: "/grant-finder/grants/illinois" },
      { label: "Grants in Maryland", href: "/grant-finder/grants/maryland" },
      { label: "Grants in New York", href: "/grant-finder/grants/new-york" },
      { label: "See all locations", href: "/grant-finder/grant-index#locations" },
    ],
  },
  {
    heading: "How to find grants",
    links: [
      { label: "Nonprofit Startup Grants", href: "https://www.zeffy.com/blog/nonprofit-start-up-grants" },
      { label: "Grant Writing Certifications", href: "https://www.zeffy.com/blog/grant-writing-certification" },
      { label: "Operational Grants", href: "https://www.zeffy.com/blog/operational-grants-for-nonprofits" },
      { label: "Corporate Grants", href: "https://www.zeffy.com/blog/corporate-grants-for-nonprofits" },
      { label: "Marketing Grants", href: "https://www.zeffy.com/blog/marketing-grants-for-nonprofits" },
    ],
  },
  {
    heading: "Zeffy",
    links: [
      { label: "What is Zeffy?", href: "https://www.zeffy.com/" },
      { label: "How is Zeffy free?", href: "https://www.zeffy.com/home/free-online-fundraising-platform" },
      { label: "Switch to zero-fee fundraising", href: "https://www.zeffy.com/register" },
    ],
  },
];

export const Footer: FC = () => {
  const year = new Date().getFullYear();

  return (
    <Box component="footer">
      <Box sx={{ overflow: "hidden", lineHeight: 0 }}>
        <Box
          component="svg"
          viewBox="0 0 1440 120"
          preserveAspectRatio="none"
          sx={{ display: "block", width: "100%", height: { xs: 80, md: 120 } }}
        >
          <path d="M0,120 L0,70 C240,100 480,40 720,70 C960,100 1200,40 1440,70 L1440,120 Z" fill={FOOTER_BACKGROUND} />
        </Box>
      </Box>
      <Box sx={{ backgroundColor: FOOTER_BACKGROUND, px: { xs: 3, md: 10 }, py: { xs: 5, md: 7.5 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
        >
          <Box component="a" href="/grant-finder" sx={{ display: "inline-flex", lineHeight: 0 }}>
            <Box
              component="img"
              src="https://cdn.prod.website-files.com/60af7f6d21134db12548f5b9/624db41f4ad2bf4abbdaeb80_Zeffy-HiRez-Logo-White.webp"
              alt="Zeffy"
              loading="lazy"
              sx={{ width: 163, height: 50, objectFit: "contain" }}
            />
          </Box>
          <Stack direction="row" spacing={1.25}>
            {SOCIAL_LINKS.map((social) => (
              <Box
                key={social.label}
                component="a"
                href={social.href}
                target="_blank"
                rel="noreferrer"
                aria-label={social.label}
                sx={{ display: "inline-flex", lineHeight: 0 }}
              >
                <Box component="img" src={social.icon} alt="" loading="lazy" sx={{ width: 40, height: 40 }} />
              </Box>
            ))}
          </Stack>
        </Stack>

        <Box sx={{ height: "2px", backgroundColor: FOOTER_DIVIDER, my: 5 }} />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
            gap: { xs: 4, md: 3 },
          }}
        >
          {LINK_COLUMNS.map((column) => (
            <Stack key={column.heading} spacing={2}>
              <DashboardTypography
                variant="caption"
                sx={{
                  color: HEADING_TEAL,
                  fontSize: 14,
                  fontWeight: 500,
                  letterSpacing: "0.8px",
                  textTransform: "uppercase",
                }}
              >
                {column.heading}
              </DashboardTypography>
              {column.links.map((link) => (
                <Box
                  key={link.label}
                  component="a"
                  href={link.href}
                  sx={{
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline", textDecorationColor: "#FFFFFF" },
                  }}
                >
                  <DashboardTypography variant="caption" sx={{ color: "#FFFFFF", fontSize: 14 }}>
                    {link.label}
                  </DashboardTypography>
                </Box>
              ))}
            </Stack>
          ))}
        </Box>

        <Stack direction="row" spacing={5} alignItems="center" justifyContent="space-between" sx={{ pt: 5 }}>
          <Box
            component="img"
            src="https://cdn.prod.website-files.com/60af7f6d21134db12548f5b9/62cfc4b3cc2ddd73992d5304_bcorp.svg"
            alt="Certified B Corporation"
            loading="lazy"
            sx={{ width: 36 }}
          />
          <DashboardTypography variant="body1" sx={{ color: "#FFFFFF" }}>
            © {year} Zeffy, inc. All rights reserved.
          </DashboardTypography>
        </Stack>
      </Box>
    </Box>
  );
};
